import { ApolloClient, MutationUpdaterFn } from "@apollo/client";
import {
  CheckCircle,
  Clock,
  HelpCircle,
  Lock,
  LucideIcon,
  CornerUpLeft,
  RotateCcw,
  XCircle,
} from "lucide-react";
import moment from "moment";
import { ALL_TRANSACTIONS } from "../../graphql/transaction/query";
import {
  AllCompanyTransactions,
  AllCompanyTransactionsVariables,
  AllCompanyTransactions_AllCompanyTransactions_transactions,
} from "../../graphql/transaction/types/AllCompanyTransactions";
import { DownloadAccountStatementVariables } from "../../graphql/transaction/types/DownloadAccountStatement";
import {
  MarkTransactionAsRead,
  MarkTransactionAsRead_markTransactionNotificationAsRead,
} from "../../graphql/transaction/types/MarkTransactionAsRead";
import { Transaction_transaction } from "../../graphql/transaction/types/Transaction";
import { IItems } from "../../interface/CommonInteface";
import { FileInterface } from "../../interface/FileInterface";
import { DEFAULT_NAME } from "../../utils/constant";
import {
  CATEGORY_TRANSACTION,
  DEFAULT_NOT_VALIDATED_TR_ARGS,
  DEFAULT_VALIDATED_TR_ARGS,
  MODALITY,
  TRANSACTION_MODALITY,
  TRANSACTION_STATE,
  TR_INVALIDATED_SIZE,
} from "../../utils/transaction";
import { LastTransaction_lastTransaction } from "../../graphql/graphql_transaction/types/LastTransaction";
import { TransactionInfo_transactionInfo } from "../../graphql/graphql_transaction/types/TransactionInfo";
import { ListCategory_listCategory } from "../../graphql/graphql_transaction/types/ListCategory";
import {
  CANCELLED_LIKE,
  FINAL_OK,
  IN_PROGRESS,
  PAYMENT_TYPES,
  REFUND_TYPES,
  REJECTED_LIKE,
  TRANSACTION_STATUS,
  TRANSACTION_TYPE,
  TRX_METHOD_ENUM,
  TRX_STATUS_ENUM,
  TRX_TYPE_ENUM,
} from "../../enums/transaction";
import { addDayToCurrentDate } from "../../utils/date";
import { IStatementFilter } from "../Common/GetStatement/GetStatement";
import { BeneficiaryInfo } from "../../graphql/graphql_transaction_linkcy/beneficiary/types/BeneficiaryInfo";
import { document_status } from "../../enums/document";
import { BillingType } from "../../enums/billing";
interface TransactionInfoIntoCardInterface {
  icon: string;
  modality: string;
  counterpart: string;
  amount: string;
  dateTransaction: string;
  transactionId: string;
  cardOwner: {
    name: string;
    firstName: string;
  };
  cardPan: string;
}
export interface TransactionToUpdate {
  id: string | null;
  name: string;
  title: string;
  bic: string;
  iban: string;
  categoryId: string | null;
  recurrenceId: string | null;
  beneficiaryId: string | null;

  note: string;
  type: number;
  amount: string;
  dateTransaction: Date;
  endTransaction: Date;
}
export const ARRAY_NOT_LENGTH = 0;

export const transactionFileInit: FileInterface = {
  filename: "",
  filetype: "",
  file: [],
};

export const displayTransactionInfoCard = (
  transaction:
    | Transaction_transaction
    | TransactionInfo_transactionInfo
    | AllCompanyTransactions_AllCompanyTransactions_transactions
    | null,
): TransactionInfoIntoCardInterface => {
  let transactionCardInfo: TransactionInfoIntoCardInterface = {
    icon: "",
    modality: "",
    counterpart: "",
    amount: "",
    dateTransaction: "",
    transactionId: "",
    cardOwner: { name: "", firstName: "" },
    cardPan: "xxxx-xxxx-xxxx-xxxx",
  };
  if (transaction) {
    const beneficiaryProvider = transaction.beneficiary;
    const categoryProvider = transaction.category;
    transactionCardInfo.counterpart =
      (beneficiaryProvider && beneficiaryProvider.name) || DEFAULT_NAME;
    if (categoryProvider && categoryProvider.constant_code)
      transactionCardInfo.icon = getIconCategoryByConstantCode(
        categoryProvider.constant_code,
      );
    transactionCardInfo.amount = transaction.amount || "";
    transactionCardInfo.dateTransaction = transaction.dateTransaction || "";
    transactionCardInfo.transactionId = transaction.id || "";
    transactionCardInfo.modality =
      (transaction.modality !== null &&
        transaction.modality > -1 &&
        getModality(transaction.modality, transaction.transactionType)) ||
      "";

    // The two keys bellow do not exist on AllCompanyTransactions_AllCompanyTransactions_transactions type
    if ("account" in transaction && "card" in transaction) {
      if (transaction.account && transaction.account.person) {
        const { person } = transaction.account;
        if (person) {
          transactionCardInfo.cardOwner.name = person.name || "";
          transactionCardInfo.cardOwner.firstName = person.firstName || "";
        }
      }
      if (transaction.card && transaction.card.maskedPan) {
        const maskedPan = transaction.card.maskedPan;
        transactionCardInfo.cardPan = `xxxx-xxxx-xxxx-${maskedPan.slice(-4)}`;
      }
    }
  }
  return transactionCardInfo;
};

export const getTransactionToUpdate = (
  transaction: AllCompanyTransactions_AllCompanyTransactions_transactions | null,
) => {
  let init: TransactionToUpdate = {
    id: null,
    name: "",
    title: "",
    bic: "",
    iban: "",
    categoryId: null,
    recurrenceId: null,
    beneficiaryId: null,
    note: "",
    type: 0,
    amount: "",
    dateTransaction: moment().toDate(),
    endTransaction: moment().add(1, "weeks").toDate(),
  };
  if (transaction) {
    const {
      id,
      note,
      amount,
      dateTransaction,
      category,
      recurrence,
      beneficiary,
    } = transaction;
    init.id = id ? id : null;
    init.amount = amount ? amount : "";
    init.dateTransaction = dateTransaction ? dateTransaction : new Date();
    init.note = note ? note : "";
    if (beneficiary) {
      const { id: beneficiaryId, name, iban, bic } = beneficiary;
      init.beneficiaryId = beneficiaryId ? beneficiaryId : null;
      init.name = name ? name : "";
      init.iban = iban ? iban : "";
      init.bic = bic ? bic : "";
    }
    if (recurrence) {
      const { id: recurrenceId, type, dateFinale } = recurrence;
      init.recurrenceId = recurrenceId ? recurrenceId : null;
      init.type = type ? type : 0;
      init.endTransaction = dateFinale ? dateFinale : new Date();
    }
    if (category) {
      const { id: categoryId } = category;
      init.categoryId = categoryId ? categoryId : null;
    }
  }
  return init;
};

// Default filter transaction params
export const defaultFilterArgs: AllCompanyTransactionsVariables = {
  modality: null,
  modalityType: null,
  categoryId: null,
  recurrenceType: null,
};

const compareDate = (dateTransaction: any): boolean => {
  const date = new Date() as any;
  const _dateTransaction = new Date(dateTransaction) as any;
  const diffTime = Math.abs(_dateTransaction - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays === 0;
};

export const updateCacheAfterTimerFinished = (
  client: ApolloClient<any>,
  newTransaction: AllCompanyTransactions_AllCompanyTransactions_transactions,
) => {
  if (newTransaction) {
    // Remove transaction finished timer from invalidated transaction
    const invalidatedTr = client.readQuery<
      AllCompanyTransactions,
      AllCompanyTransactionsVariables
    >({
      query: ALL_TRANSACTIONS,
      variables: {
        ...DEFAULT_NOT_VALIDATED_TR_ARGS,
        size: TR_INVALIDATED_SIZE,
        page: null,
      },
    });
    if (
      invalidatedTr &&
      invalidatedTr.AllCompanyTransactions &&
      invalidatedTr.AllCompanyTransactions.transactions
    ) {
      const _invalidatedTr =
        invalidatedTr.AllCompanyTransactions.transactions.map((transaction) => {
          if (transaction && transaction.id === newTransaction.id) {
            if (
              !transaction.recurrence &&
              compareDate(newTransaction.dateTransaction)
            ) {
              transaction.transactionState = TRANSACTION_STATE.TERMINATED;
            } else if (transaction.source) {
              transaction.transactionState = TRANSACTION_STATE.PENDING;
            } else {
              transaction.transactionState = TRANSACTION_STATE.SCHEDULED;
            }
          }
          return transaction;
        });
      client.writeQuery<
        AllCompanyTransactions,
        AllCompanyTransactionsVariables
      >({
        query: ALL_TRANSACTIONS,
        variables: {
          ...DEFAULT_NOT_VALIDATED_TR_ARGS,
          size: TR_INVALIDATED_SIZE,
          page: null,
        },
        data: {
          AllCompanyTransactions: {
            ...invalidatedTr.AllCompanyTransactions,
            transactions: [
              ..._invalidatedTr.filter(
                (transaction) =>
                  transaction &&
                  transaction.transactionState !== TRANSACTION_STATE.TERMINATED,
              ),
            ],
          },
        },
      });

      // Add transaction finished timer into validated transaction
      const validatedTr = client.readQuery<
        AllCompanyTransactions,
        AllCompanyTransactionsVariables
      >({
        query: ALL_TRANSACTIONS,
        variables: { ...DEFAULT_VALIDATED_TR_ARGS },
      });
      if (
        validatedTr &&
        validatedTr.AllCompanyTransactions &&
        validatedTr.AllCompanyTransactions.transactions
      ) {
        if (
          !newTransaction.recurrence &&
          compareDate(newTransaction.dateTransaction)
        ) {
          newTransaction.transactionState = TRANSACTION_STATE.TERMINATED;
          client.writeQuery<
            AllCompanyTransactions,
            AllCompanyTransactionsVariables
          >({
            query: ALL_TRANSACTIONS,
            variables: {
              ...DEFAULT_VALIDATED_TR_ARGS,
            },
            data: {
              AllCompanyTransactions: {
                ...validatedTr.AllCompanyTransactions,
                transactions: [
                  newTransaction,
                  ...validatedTr.AllCompanyTransactions.transactions,
                ],
              },
            },
          });
        }
      }
    }
  }
};

export enum TRANSACTION_PAYOUT_TYPE {
  TOP_UP = "TOP_UP",
  REFUND = "REFUND",
  PAYIN_FEE = "PAYIN_FEE",
  SAPHEER_ABONNEMENT = "SAPHEER_ABONNEMENT",
  VIREMENT = "VIREMENT",
  WITHDRAWAL_FRAIS = "WITHDRAWAL_FRAIS",
  VIREMENT_INST = "VIREMENT_INST",
  NEW_SAPHEER_ABONNEMENT = "NEW_SAPHEER_ABONNEMENT",
}

export const getTransactionType = (
  methodeId: number,
  typesId?: number | null,
  billingType?: string | null,
) => {
  if (methodeId == TRX_METHOD_ENUM.INTERNAL) {
    if (billingType) {
      if (billingType === BillingType.SUBSCRIPTION) {
        return "transaction.modality.abonnement";
      } else {
        return "transaction.modality.frais";
      }
    }
    return "transaction.modality.transfer";
  }
  if (methodeId == TRX_METHOD_ENUM.SCT_SEPA) {
    return "transaction.modality.payin";
  }
  if (methodeId == TRX_METHOD_ENUM.INST_SEPA) {
    return "transaction.modality.fast.payment";
  }
  if (
    methodeId == TRX_METHOD_ENUM.SDD ||
    methodeId == TRX_METHOD_ENUM.DDI_INTERNAL
  ) {
    return "transaction.modality.payout";
  }
  if (methodeId == TRX_METHOD_ENUM.CARD) {
    // return "transaction.card.withdrawal";
    return "transaction.card.payment";
  }
  return "-";
};

export const getIcon = (methodeId: number) => {
  if (
    methodeId == TRX_METHOD_ENUM.INTERNAL ||
    methodeId == TRX_METHOD_ENUM.SCT_SEPA
  ) {
    return "✉️";
  }
  if (methodeId == TRX_METHOD_ENUM.INST_SEPA) {
    return "⚡";
  }
  if (
    methodeId == TRX_METHOD_ENUM.SDD ||
    methodeId == TRX_METHOD_ENUM.DDI_INTERNAL
  ) {
    return "📑";
  }
  if (methodeId == TRX_METHOD_ENUM.CARD) {
    return "💳";
  }
  return "💸";
};

export const getStatusStyle = (
  statusId: TRX_STATUS_ENUM | number | null,
  typeId: TRX_TYPE_ENUM | number | null,
) => {
  if (statusId == null) {
    return { color: "#6b7280", label: "Inconnue", icon: HelpCircle };
  }

  const status = statusId as TRX_STATUS_ENUM;
  const type = typeId as TRX_TYPE_ENUM;

  // Refusé
  if (REJECTED_LIKE.has(status)) {
    return { color: "#dc2626", label: "Refusé", icon: XCircle };
  }

  // Annulé
  if (CANCELLED_LIKE.has(status)) {
    return { color: "#6B7280", label: "Annulé", icon: CornerUpLeft };
  }

  // Problème
  if (status === TRX_STATUS_ENUM.SOME_PROBLEMS) {
    return { color: "#6B7280", label: "Problème", icon: HelpCircle };
  }

  // En cours
  if (IN_PROGRESS.has(status)) {
    // Spécifique aux cartes : autorisation visible comme "Autorisé"
    if (type === TRX_TYPE_ENUM.CARD_AUTH) {
      return { color: "#3b82f6", label: "Autorisé", icon: Lock };
    }
    return { color: "#f97316", label: "En cours", icon: Clock };
  }

  // Final OK
  if (FINAL_OK.has(status)) {
    // Remboursement / reversal
    if (REFUND_TYPES.has(type)) {
      return { color: "#9333ea", label: "Remboursé", icon: RotateCcw };
    }

    // Carte : clearing = payé
    if (type === TRX_TYPE_ENUM.CARD_CLEARING) {
      return { color: "#16a34a", label: "Payé", icon: CheckCircle };
    }

    // Autorisation carte validée (au cas où statut final + auth)
    if (type === TRX_TYPE_ENUM.CARD_AUTH) {
      return { color: "#3b82f6", label: "Autorisé", icon: Lock };
    }

    // Par défaut validé
    return { color: "#16a34a", label: "Validé", icon: CheckCircle };
  }

  // Défaut
  return { color: "#6b7280", label: "Inconnue", icon: HelpCircle };
};

export const getFileColor = (status?: string | null) => {
  switch (status) {
    // Validé
    case document_status.REQUESTED:
      return "#dc2626";

    // En cours
    case document_status.UPLOADED:
      return "#16a34a";

    // Par défaut
    default:
      return "";
  }
};

export const getModality = (
  modality: number | null,
  type?: string | null,
): string => {
  if (type && type === TRANSACTION_PAYOUT_TYPE.PAYIN_FEE) {
    // Prelèvement
    return "transaction.payout.type";
  }
  if (type && type === TRANSACTION_PAYOUT_TYPE.SAPHEER_ABONNEMENT) {
    // Abonnement
    return "transaction.payout.abonnement";
  }
  return MODALITY.filter(
    (modalityItem) => modalityItem.modality === modality,
  )[0].value as string;
};

export const getIconCategoryByConstantCode = (constantCode: string): string => {
  return CATEGORY_TRANSACTION[constantCode];
};

export const searchBeneficiary = (
  value: string,
  beneficiaries: (BeneficiaryInfo | null)[],
): (BeneficiaryInfo | null)[] => {
  return beneficiaries.filter((beneficiary: BeneficiaryInfo | null) => {
    if (beneficiary && beneficiary.name) {
      const _name = beneficiary.name.toLowerCase();
      const _value = value.toLowerCase();
      return _name.includes(_value);
    }
  });
};

export const searchTransaction = (
  value: string,
  transactions: (AllCompanyTransactions_AllCompanyTransactions_transactions | null)[],
): (AllCompanyTransactions_AllCompanyTransactions_transactions | null)[] => {
  const result = transactions.filter(
    (
      transaction: AllCompanyTransactions_AllCompanyTransactions_transactions | null,
    ) => {
      if (
        transaction &&
        transaction.beneficiary &&
        transaction.category &&
        transaction.beneficiary.name &&
        transaction.category.name
      ) {
        const { name: beneficiaryName } = transaction.beneficiary;
        const { name: categoryName } = transaction.category;
        const _beneficiaryName = beneficiaryName.toLowerCase();
        const _categoryName = categoryName.toLowerCase();
        const _value = value.toLowerCase();
        return (
          _beneficiaryName.includes(_value) || _categoryName.includes(_value)
        );
      }
    },
  );
  return result;
};

export const categoriesAsIItems = (
  categories: (ListCategory_listCategory | null)[],
): IItems[] => {
  return categories
    .map((category: ListCategory_listCategory | null) => {
      if (category && category.name && category.id)
        return { id: category.id, label: category.name } as IItems;
    })
    .filter(
      (value: IItems | undefined) => typeof value !== "undefined",
    ) as IItems[];
};

export interface InvalidatedTransactionSplitInterface {
  haveTimer: LastTransaction_lastTransaction[];
  doNotHaveTimer: LastTransaction_lastTransaction[];
}

export const splitInvalidatedTransaction = (
  transactions: Array<LastTransaction_lastTransaction | null> | null,
): InvalidatedTransactionSplitInterface => {
  const tr: InvalidatedTransactionSplitInterface = {
    haveTimer: [],
    doNotHaveTimer: [],
  };
  if (transactions) {
    transactions.map((transaction: LastTransaction_lastTransaction | null) => {
      if (transaction) {
        if (
          transaction.timer ||
          transaction.transactionState === TRANSACTION_STATE.SCHEDULED ||
          transaction.transactionState === TRANSACTION_STATE.PENDING
        ) {
          tr.haveTimer.push(transaction);
        } else if (
          transaction.transactionState !== TRANSACTION_STATE.CANCELLED
        ) {
          tr.doNotHaveTimer.push(transaction);
        }
      }
    });
  }
  return tr;
};

interface TransactionModalitySplitInterface {
  payin: Array<AllCompanyTransactions_AllCompanyTransactions_transactions | null>;
  payout: Array<AllCompanyTransactions_AllCompanyTransactions_transactions | null>;
}

export const splitTransactionModality = (
  transactions: Array<AllCompanyTransactions_AllCompanyTransactions_transactions | null>,
): TransactionModalitySplitInterface => {
  const splitTr: TransactionModalitySplitInterface = { payin: [], payout: [] };

  if (!!transactions.length) {
    transactions.forEach(
      (
        transaction: AllCompanyTransactions_AllCompanyTransactions_transactions | null,
      ) => {
        if (transaction) {
          if (transaction.modality === TRANSACTION_MODALITY.PAYOUT) {
            splitTr.payout.push(transaction);
            // FIX ME: all transaction payin?
          } else if (
            transaction.modality === TRANSACTION_MODALITY.PAYIN ||
            transaction.modality === TRANSACTION_MODALITY.TRANSFERT
          ) {
            splitTr.payin.push(transaction);
          }
        }
      },
    );
  }

  return splitTr;
};

export const formatSecondForCountdown = (second: number): string => {
  const minutes = Math.floor(second / 60).toString();
  const seconds = (second % 60).toString();
  const formattedMinute = minutes.length === 1 ? `0${minutes}` : minutes;
  const formattedSecond = seconds.length === 1 ? `0${seconds}` : seconds;

  return `${formattedMinute} : ${formattedSecond}`;
};

export const transactionUpdateMessageId = (modality: number): string => {
  switch (modality) {
    case TRANSACTION_MODALITY.PAYIN:
      return "transaction.success.payin.update";
    case TRANSACTION_MODALITY.PAYOUT:
      return "transaction.success.payout.update";
    case TRANSACTION_MODALITY.PAIEMENT || TRANSACTION_MODALITY.WITHDRAWAL:
      return "transaction.success.card.update";
    default:
      return "transaction.success.update";
  }
};

export const getNotViewedTransactions = (
  transactions: Array<AllCompanyTransactions_AllCompanyTransactions_transactions | null>,
): Array<AllCompanyTransactions_AllCompanyTransactions_transactions | null> => {
  if (!transactions.length) {
    return [];
  }
  return transactions.filter((tr) => {
    if (tr && tr.viewed === false) {
      return true;
    }
    return false;
  });
};

export const getTransactionsIds = (
  transactions: Array<AllCompanyTransactions_AllCompanyTransactions_transactions | null>,
): string[] => {
  if (!transactions.length) {
    return [];
  }
  return transactions
    .filter((tr) => {
      if (tr && tr.id) {
        return true;
      }
      return false;
    })
    .map((tr) => {
      if (tr && tr.id) {
        return tr.id;
      }
      return "";
    });
};

const transactionIdIntoMarkedList = (
  transactions: Array<MarkTransactionAsRead_markTransactionNotificationAsRead | null>,
  transactionId: string,
): boolean => {
  return !!transactions.filter((tr) => {
    if (tr && tr.id === transactionId) {
      return true;
    }
    return false;
  }).length;
};

export const updateNotViewedTrCacheAfterMark: MutationUpdaterFn<
  MarkTransactionAsRead
> = (cache, { data }) => {};

const SPECIFIC_DATE_ARGS = "-1";

export const getFilterStatementState = (
  currentStatement: IStatementFilter,
  inputTarget: { name: string; value: string },
): IStatementFilter => {
  const { name, value } = inputTarget;
  switch (name) {
    case "statementPeriod":
      // None period
      if (value == "-1") {
        return currentStatement;
      }
      const dateStart = addDayToCurrentDate(new Date(), -value);
      return {
        ...currentStatement,
        statementPeriod: value,
        startDate: dateStart,
        endDate: new Date(),
      };

    case "startDate":
      return {
        ...currentStatement,
        statementPeriod: "-1",
        startDate: value,
      };
    case "endDate":
      return {
        ...currentStatement,
        statementPeriod: "-1",
        endDate: value,
      };
    default:
      return {
        ...currentStatement,
        [name]: value === "-1" ? null : +value,
      };
  }
};

export const getDateStatementFilter = (
  filterArgs: DownloadAccountStatementVariables,
): string => {
  return filterArgs.totalDay
    ? filterArgs.totalDay.toString() // months
    : filterArgs.startDate // specific date
    ? SPECIFIC_DATE_ARGS
    : "-1"; // none
};

export const isStatementPeriodDisplayed = (periodArg: string): boolean =>
  periodArg === SPECIFIC_DATE_ARGS;
